import { useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';
import { winsorizationFrontendConfig } from '@gonfalon/dogfood-flags/dogfood-flags';
import { HelpTooltip } from '@gonfalon/launchpad-experimental/HelpTooltip';
import { isLDHosted } from '@gonfalon/metrics/isLDHosted';
import { getMetricDataSourcesQuery } from '@gonfalon/rest-api/queries/metricDataSource';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { Checkbox, FieldError, FieldGroup, Input, Label, NumberField } from '@launchpad-ui/components';
import { useQuery } from '@tanstack/react-query';

import type { MetricForm } from '../formUtils';

import styles from './WinsorizationFields.module.css';

/** Precision for winsorization percentile inputs (5 decimal places). */
const WINSORIZATION_PERCENTILE_SIGFIG = 0.00001;

function validatePercentileBound(
  boundType: 'Lower' | 'Upper',
  bound?: number,
  otherBound?: number,
): string | undefined {
  const boundLabel = `${boundType} bound`;

  if (bound === undefined || Number.isNaN(bound)) {
    return `${boundLabel} is required`;
  }
  if (bound < 0) {
    return `${boundLabel} cannot be less than 0`;
  }
  if (bound > 100) {
    return `${boundLabel} cannot be greater than 100`;
  }
  if (otherBound !== undefined && !Number.isNaN(otherBound)) {
    if (boundType === 'Lower' && bound >= otherBound) {
      return 'Lower bound must be less than upper bound';
    }
    if (boundType === 'Upper' && bound <= otherBound) {
      return 'Upper bound must be greater than lower bound';
    }
  }
  return undefined;
}

export { validatePercentileBound };

function getLowerPercentileError(lower?: number, upper?: number): string | undefined {
  return validatePercentileBound('Lower', lower, upper);
}

function getUpperPercentileError(upper?: number, lower?: number): string | undefined {
  return validatePercentileBound('Upper', upper, lower);
}

export function useWinsorizationEnabled(dataSourceKey?: string): boolean {
  const projectKey = useProjectKey();
  const { data: dataSourcesData } = useQuery(getMetricDataSourcesQuery({ projKey: projectKey }));
  const winsorizationConfig = winsorizationFrontendConfig();

  let integrationKey: string | undefined;
  if (isLDHosted(dataSourceKey)) {
    integrationKey = 'launchdarkly';
  } else {
    const dataSource = dataSourcesData?.items?.find((ds) => ds.key === dataSourceKey);
    integrationKey = dataSource?.integrationKey;
  }

  return integrationKey ? winsorizationConfig[integrationKey] === true : false;
}

// ─── WinsorizationBoundsSection ───────────────────────────────────────────

export interface WinsorizationBoundsSectionProps {
  context: UseFormReturn<MetricForm>;
  /** Form field path for the lower percentile value. */
  lowerControlName: string;
  /** Form field path for the upper percentile value. */
  upperControlName: string;
  /** Current watched value of the lower percentile (for cross-field validation). */
  lowerPercentile?: number;
  /** Current watched value of the upper percentile (for cross-field validation). */
  upperPercentile?: number;
  /** When true, omit the explanatory help text above the inputs. */
  hideHelpText?: boolean;
}

/**
 * Renders the lower/upper percentile bound number inputs.
 * Both inputs are always visible; callers control when the section is shown.
 * Cross-field validation uses the watched percentile props so each Controller
 * can validate against the other field's current value.
 */
export function WinsorizationBoundsSection({
  context,
  lowerControlName,
  upperControlName,
  lowerPercentile,
  upperPercentile,
  hideHelpText,
}: WinsorizationBoundsSectionProps) {
  const { control } = context;

  return (
    <>
      {!hideHelpText && (
        <p className={styles.helpText}>Outlier values are clamped to percentile bounds before metric calculation.</p>
      )}
      <div className={styles.inputsRow}>
        <Controller
          control={control}
          name={lowerControlName as never}
          rules={{
            validate: (val) => getLowerPercentileError(val as number | undefined, upperPercentile) ?? true,
          }}
          render={({ field: { onChange, value, ref, ...field }, fieldState }) => (
            <NumberField
              {...field}
              value={(value as number | undefined) ?? NaN}
              onChange={onChange}
              isInvalid={!!fieldState.error}
              minValue={0}
              maxValue={100}
              step={WINSORIZATION_PERCENTILE_SIGFIG}
              formatOptions={{ maximumFractionDigits: 5 }}
              className={styles.percentileInput}
            >
              <Label>Lower bound</Label>
              <div className={styles.inputWrapper}>
                <Input ref={ref} />
                <span className={styles.percentSymbol}>%</span>
              </div>
              {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
            </NumberField>
          )}
        />

        <Controller
          control={control}
          name={upperControlName as never}
          rules={{
            validate: (val) => getUpperPercentileError(val as number | undefined, lowerPercentile) ?? true,
          }}
          render={({ field: { onChange, value, ref, ...field }, fieldState }) => (
            <NumberField
              {...field}
              value={(value as number | undefined) ?? NaN}
              onChange={onChange}
              isInvalid={!!fieldState.error}
              minValue={0}
              maxValue={100}
              step={WINSORIZATION_PERCENTILE_SIGFIG}
              formatOptions={{ maximumFractionDigits: 5 }}
              className={styles.percentileInput}
            >
              <Label>Upper bound</Label>
              <div className={styles.inputWrapper}>
                <Input ref={ref} />
                <span className={styles.percentSymbol}>%</span>
              </div>
              {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
            </NumberField>
          )}
        />
      </div>
    </>
  );
}

// ─── WinsorizationFields ──────────────────────────────────────────────────

interface WinsorizationFieldsProps {
  context: UseFormReturn<MetricForm>;
}

export const WinsorizationFields: React.FC<WinsorizationFieldsProps> = ({ context }) => {
  const { control, setValue, unregister } = context;

  const [measureType, dataSourceKey, lowerPercentile, upperPercentile] = useWatch({
    control,
    name: ['measureType', 'dataSourceKey', 'winsorization.lowerPercentile', 'winsorization.upperPercentile'],
  });

  const isEnabled = lowerPercentile !== undefined || upperPercentile !== undefined;
  const isWinsorizationEnabledForDataSource = useWinsorizationEnabled(dataSourceKey);
  const isWinsorizationEligible = measureType === 'count' || measureType === 'value';
  const showWinsorization = isWinsorizationEnabledForDataSource && isWinsorizationEligible;

  // Clear winsorization form state when the section becomes hidden
  useEffect(() => {
    if (!showWinsorization) {
      unregister(['winsorization.lowerPercentile', 'winsorization.upperPercentile'], {
        keepError: false,
        keepDirty: false,
        keepTouched: false,
      });
    }
  }, [showWinsorization, unregister]);

  // Populate missing bounds with defaults when editing a metric with partial winsorization
  useEffect(() => {
    if (isEnabled && showWinsorization) {
      if (lowerPercentile === undefined) {
        setValue('winsorization.lowerPercentile', 0, { shouldDirty: false });
      }
      if (upperPercentile === undefined) {
        setValue('winsorization.upperPercentile', 100, { shouldDirty: false });
      }
    }
  }, [isEnabled, showWinsorization, lowerPercentile, upperPercentile, setValue]);

  const handleEnableChange = (isSelected: boolean) => {
    if (isSelected) {
      setValue('winsorization.lowerPercentile', 0, { shouldDirty: true });
      setValue('winsorization.upperPercentile', 100, { shouldDirty: true });
    } else {
      unregister(['winsorization.lowerPercentile', 'winsorization.upperPercentile'], {
        keepError: false,
        keepDirty: false,
        keepTouched: false,
      });
    }
  };

  if (!showWinsorization) {
    return null;
  }

  return (
    <FieldGroup className={styles.winsorizationSection}>
      <div className={styles.labelRow}>
        <Checkbox isSelected={isEnabled} onChange={handleEnableChange}>
          Enable winsorization
        </Checkbox>
        <HelpTooltip placement="right">
          Cap outlier values at specified percentiles to reduce the impact of extreme measurements on experiment
          results.
        </HelpTooltip>
      </div>

      {isEnabled && (
        <div className={styles.winsorizationContent}>
          <WinsorizationBoundsSection
            context={context}
            lowerControlName="winsorization.lowerPercentile"
            upperControlName="winsorization.upperPercentile"
            lowerPercentile={lowerPercentile}
            upperPercentile={upperPercentile}
          />
        </div>
      )}
    </FieldGroup>
  );
};
